import { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { api } from '../../lib/api';

type Treffer = { id: string; name: string; email?: string | null };
type Vorschau = {
  ziel: { id: number; name: string; email?: string | null };
  quelle: { id: number; name: string; email?: string | null };
  hindernisse: string[];
  umgekehrtMoeglich: boolean;
  moeglich: boolean;
  profile: Array<{ alt: number; neu: number; name: string }>;
  verweise: Record<string, number>;
};

type Props = {
  open: boolean;
  zielId: string;
  zielName: string;
  onClose: () => void;
  /** Nach dem Zusammenführen: die Person, die geblieben ist. */
  onZusammengefuehrt: (personId: number) => void;
};

/**
 * Doppelte Personen zusammenführen (T-0079).
 *
 * Die gewählte Person geht in dieser auf: Vorgänge, Historie, Profile und
 * Abrechnung wandern hierher, die Dublette wird gelöscht. Nicht umkehrbar —
 * deshalb erst die Vorschau, dann der Grund.
 */
export default function PersonZusammenfuehrenDialog({ open, zielId, zielName, onClose, onZusammengefuehrt }: Props) {
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState<Treffer[]>([]);
  const [sucht, setSucht] = useState(false);
  const [quelle, setQuelle] = useState<Treffer | null>(null);
  const [vorschau, setVorschau] = useState<Vorschau | null>(null);
  const [umgedreht, setUmgedreht] = useState(false);
  const [grund, setGrund] = useState('');
  const [arbeitet, setArbeitet] = useState(false);

  useEffect(() => {
    if (!open) {
      setSuche('');
      setTreffer([]);
      setQuelle(null);
      setVorschau(null);
      setUmgedreht(false);
      setGrund('');
    }
  }, [open]);

  useEffect(() => {
    const text = suche.trim();
    if (!open || text.length < 2 || quelle) {
      setTreffer([]);
      return;
    }
    const t = setTimeout(async () => {
      setSucht(true);
      try {
        const params = new URLSearchParams({ q: text, limit: '10', all_standorte: '1' });
        const liste = await api.get<any[]>(`/api/personen?${params.toString()}`);
        setTreffer(
          (liste || [])
            .filter((p) => p && p.isSubprofile !== true)
            .map((p) => ({
              id: String(p.id ?? p.wpUserId ?? ''),
              name: String(p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Ohne Namen'),
              email: p.email ?? null,
            }))
            .filter((p) => p.id && p.id !== String(zielId)),
        );
      } catch {
        setTreffer([]);
      } finally {
        setSucht(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [suche, open, quelle, zielId]);

  const ziel = umgedreht && quelle ? quelle.id : zielId;
  const von = umgedreht ? zielId : quelle?.id;

  useEffect(() => {
    if (!quelle || !von) {
      setVorschau(null);
      return;
    }
    let aktuell = true;
    api
      .get<Vorschau>(`/api/personen/${ziel}/zusammenfuehren/vorschau?quelle=${von}`)
      .then((daten) => {
        if (aktuell) setVorschau(daten);
      })
      .catch((e: unknown) => {
        if (aktuell) toast.error(e instanceof Error ? e.message : 'Vorschau nicht verfügbar');
      });
    return () => {
      aktuell = false;
    };
  }, [quelle, ziel, von]);

  const zusammenfuehren = async () => {
    if (!vorschau || !von) return;
    setArbeitet(true);
    try {
      const ergebnis = await api.post<{ zielId: number }>(`/api/personen/${ziel}/zusammenfuehren`, {
        quelleId: Number(von),
        grund: grund.trim(),
      });
      toast.success('Personen zusammengeführt');
      onZusammengefuehrt(ergebnis.zielId);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Zusammenführen fehlgeschlagen');
    } finally {
      setArbeitet(false);
    }
  };

  const anzahl = vorschau ? Object.values(vorschau.verweise).reduce((s, n) => s + n, 0) : 0;

  return (
    <Dialog open={open} onOpenChange={(offen) => (!offen ? onClose() : undefined)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Doppelte Person zusammenführen</DialogTitle>
          <DialogDescription>
            Die gewählte Person geht in {zielName || 'dieser Person'} auf und wird danach gelöscht. Das lässt sich nicht
            rückgängig machen.
          </DialogDescription>
        </DialogHeader>

        {!quelle ? (
          <div className="grid gap-2">
            <Label htmlFor="zf-suche">Dublette suchen</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input id="zf-suche" className="pl-8" placeholder="Name oder E-Mail" value={suche} onChange={(e) => setSuche(e.target.value)} />
            </div>
            {sucht ? <p className="text-xs text-muted-foreground">Suche …</p> : null}
            <div className="grid gap-1">
              {treffer.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setQuelle(t)}
                  className="rounded-md border px-3 py-2 text-left text-sm hover:bg-muted"
                >
                  <div className="font-medium">{t.name}</div>
                  {t.email ? <div className="text-xs text-muted-foreground">{t.email}</div> : null}
                </button>
              ))}
            </div>
          </div>
        ) : !vorschau ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Vorschau wird geladen …
          </div>
        ) : (
          <div className="grid gap-4">
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
              <span className="font-medium">{vorschau.quelle.name || `#${vorschau.quelle.id}`}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{vorschau.ziel.name || `#${vorschau.ziel.id}`}</span>
              <span className="text-muted-foreground">(bleibt)</span>
            </div>
            {vorschau.hindernisse.length > 0 ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <div className="mb-1 flex items-center gap-2 font-medium">
                  <AlertTriangle className="h-4 w-4" /> So geht es nicht
                </div>
                <ul className="list-disc pl-5">
                  {vorschau.hindernisse.map((h) => (
                    <li key={h}>{h}</li>
                  ))}
                </ul>
                {vorschau.umgekehrtMoeglich ? (
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => setUmgedreht((u) => !u)}>
                    Richtung tauschen
                  </Button>
                ) : null}
              </div>
            ) : (
              <>
                <div className="text-sm">
                  <div className="mb-1 font-medium">Profile</div>
                  <ul className="grid gap-1 text-muted-foreground">
                    {vorschau.profile.map((p) => (
                      <li key={p.alt}>
                        {p.name || `Profil ${p.alt}`}:{' '}
                        {p.neu === 1 ? 'wird mit dem eigenen Profil zusammengelegt' : `wird Profil ${p.neu}`}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-muted-foreground">{anzahl} Datensätze werden umgehängt.</p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="zf-grund">Grund</Label>
                  <Textarea id="zf-grund" rows={2} value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="z. B. doppelt angelegt bei der Anmeldung" />
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          {quelle ? (
            <Button variant="ghost" onClick={() => { setQuelle(null); setVorschau(null); setUmgedreht(false); }} disabled={arbeitet}>
              Andere Person wählen
            </Button>
          ) : null}
          <Button variant="outline" onClick={onClose} disabled={arbeitet}>Abbrechen</Button>
          <Button
            variant="destructive"
            onClick={() => void zusammenfuehren()}
            disabled={!vorschau?.moeglich || !grund.trim() || arbeitet}
          >
            Zusammenführen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
